import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, TrackByFunction, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import {
  SetConfigValuesGQL,
  SettingsConfigsGQL,
} from 'src/generated/graphql.generated';
import { SubSink } from 'subsink';

import { BrandingService } from '../services/branding.service';
import { FreyaNotificationsService } from '../services/freya-notifications.service';

interface ConfigRow {
  key: string;
  name: string;
  description: string;
  format: string;
  storage: string;
  readOnly: boolean;
  value: string | null;
  zone: string | null;
  isSetInZone: boolean;
  isInherited: boolean;
  fallbackZone: string | null;
  overridingZones: string[];
  dirty: boolean;
  saving: boolean;
  hasValue: boolean;
}

@Component({
  selector: 'app-config',
  standalone: true,
  templateUrl: './config.component.html',
  styleUrl: './config.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
  ],
})
export class ConfigComponent implements OnInit, OnDestroy {

  loading = true;

  search = '';

  allRows: ConfigRow[] = [];

  filteredRows: ConfigRow[] = [];

  currentZoneId: string | null = null;

  showAddCustom = false;
  customKey = '';
  customValue = '';
  customSaving = false;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  trackByKey: TrackByFunction<ConfigRow> = (_, row) => row.key;

  private subs = new SubSink();

  private queryRef?: ReturnType<SettingsConfigsGQL['watch']>;

  constructor(
    private branding: BrandingService,
    private getConfigGQL: SettingsConfigsGQL,
    private setConfigGQL: SetConfigValuesGQL,
    private clipboard: Clipboard,
    private notify: FreyaNotificationsService,
  ) {}

  ngOnInit(): void {
    this.subs.sink = this.branding.currentZone().subscribe((zone) => {
      this.currentZoneId = zone?.id ?? null;
      this.fetchConfigs();
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private searchFocused = false;

  reload(): void {
    this.queryRef?.refetch();
  }

  private fetchConfigs(): void {
    if (this.queryRef) {
      this.queryRef.refetch();
      return;
    }

    this.queryRef = this.getConfigGQL.watch({ keys: ['.*'] }, { fetchPolicy: 'cache-and-network' });

    this.subs.sink = this.queryRef.valueChanges.subscribe((res) => {
      this.loading = res.loading;
      if (res.loading) { return; }

      if (!this.searchFocused) {
        this.searchFocused = true;
        setTimeout(() => this.searchInput?.nativeElement?.focus());
      }

      const configs = res.data?.configs ?? [];

      // Requirement: preserve unsaved edits when the Apollo cache emits a new
      // snapshot (e.g. after saving one row). Rows the user has touched keep
      // their local value/dirty/saving state; only server-side metadata is
      // refreshed.
      const previous = new Map(this.allRows.map((r) => [r.key, r]));

      this.allRows = configs.filter((cv) => cv.definition).map((cv) => {
        const def = cv.definition!;
        const isSetInZone = cv.zone === this.currentZoneId;
        const isInherited = !!cv.fallback && !isSetInZone;
        const fallbackZone = isInherited ? (cv.zone ?? null) : (cv.fallback?.zone ?? null);
        const overridingZones = (cv.overridingConfigs ?? [])
          .map((o) => o.zone)
          .filter((z): z is string => !!z);

        const prior = previous.get(def.key);
        const preserveLocal = prior && (prior.dirty || prior.saving);

        return {
          key: def.key,
          name: def.name,
          description: def.description,
          format: def.format,
          storage: def.storage,
          readOnly: def.readOnly,
          value: preserveLocal ? prior!.value : (cv.storedValue ?? null),
          zone: cv.zone ?? null,
          isSetInZone: preserveLocal ? prior!.isSetInZone : isSetInZone,
          isInherited: preserveLocal ? prior!.isInherited : isInherited,
          fallbackZone: preserveLocal ? prior!.fallbackZone : fallbackZone,
          overridingZones,
          dirty: preserveLocal ? prior!.dirty : false,
          saving: preserveLocal ? prior!.saving : false,
          hasValue: preserveLocal ? prior!.hasValue : (cv.storedValue != null && cv.storedValue !== ''),
        };
      });

      this.applyFilter();
    });
  }

  applyFilter(): void {
    const q = this.search.toLowerCase().trim();
    if (!q) {
      this.filteredRows = [...this.allRows];
      return;
    }
    this.filteredRows = this.allRows.filter((row) =>
      row.key.toLowerCase().includes(q) ||
      row.name.toLowerCase().includes(q) ||
      row.description.toLowerCase().includes(q) ||
      (row.value ?? '').toLowerCase().includes(q),
    );
  }

  onSearchInput(): void {
    this.applyFilter();
  }

  onValueChange(row: ConfigRow): void {
    row.dirty = true;
  }

  save(row: ConfigRow): void {
    if (row.readOnly || row.saving) { return; }
    row.saving = true;

    this.setConfigGQL.mutate({
      configs: [{ key: row.key, value: row.value ?? '' }],
    }).subscribe({
      next: (res) => {
        row.saving = false;
        row.dirty = false;
        const updated = res.data?.setConfigValues?.find((v) => v.key === row.key);
        if (updated) {
          row.value = updated.value ?? null;
          row.zone = updated.zone ?? this.currentZoneId;
          row.isSetInZone = true;
          row.isInherited = false;
          row.hasValue = row.value != null && row.value !== '';
        }
        this.notify.success(`Saved ${row.name}`);
      },
      error: () => {
        row.saving = false;
        this.notify.error(`Failed to save ${row.name}`);
      },
    });
  }

  resetToParent(row: ConfigRow): void {
    if (row.saving) { return; }
    row.saving = true;

    this.setConfigGQL.mutate({
      configs: [{ key: row.key, value: '', reset: true }],
    }).subscribe({
      next: () => {
        row.saving = false;
        row.dirty = false;
        this.queryRef?.refetch();
        this.notify.success(`Reset ${row.name} to parent value`);
      },
      error: () => {
        row.saving = false;
        this.notify.error(`Failed to reset ${row.name}`);
      },
    });
  }

  resetChildren(row: ConfigRow): void {
    if (row.saving) { return; }
    row.saving = true;

    this.setConfigGQL.mutate({
      configs: [{ key: row.key, value: row.value ?? '', resetOverridingConfigs: true }],
    }).subscribe({
      next: () => {
        row.saving = false;
        this.queryRef?.refetch();
        this.notify.success(`Reset all child overrides for ${row.name}`);
      },
      error: () => {
        row.saving = false;
        this.notify.error(`Failed to reset child overrides`);
      },
    });
  }

  copyValue(row: ConfigRow): void {
    if (row.value == null) { return; }
    this.clipboard.copy(row.value);
    this.notify.success('Copied to clipboard');
  }

  copyKey(row: ConfigRow): void {
    this.clipboard.copy(row.key);
    this.notify.success('Key copied');
  }

  isSecret(row: ConfigRow): boolean {
    return row.storage === 'secret';
  }

  get visibleCount(): number {
    return this.filteredRows.length;
  }

  get totalCount(): number {
    return this.allRows.length;
  }

  overridingZonesTooltip(row: ConfigRow): string {
    return row.overridingZones.join(', ');
  }

  toggleAddCustom(): void {
    this.showAddCustom = !this.showAddCustom;
    this.customKey = '';
    this.customValue = '';
  }

  saveCustom(): void {
    const key = this.customKey.trim();
    // Requirement: custom keys must match the dotted namespace.name form used
    // by config definitions so they remain discoverable in this table.
    if (!key || this.customSaving) { return; }
    if (!/^[A-Za-z][\w-]*(\.[\w-]+)+$/.test(key)) {
      this.notify.error('Enter a key in the form namespace.name');
      return;
    }
    this.customSaving = true;

    this.setConfigGQL.mutate({
      configs: [{ key, value: this.customValue }],
    }).subscribe({
      next: () => {
        this.customSaving = false;
        this.showAddCustom = false;
        this.customKey = '';
        this.customValue = '';
        this.queryRef?.refetch();
        this.notify.success(`Saved custom config`);
      },
      error: () => {
        this.customSaving = false;
        this.notify.error(`Failed to save custom config`);
      },
    });
  }
}